import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft, ArrowUpDown, Calendar, Check, ChevronRight, Clock,
    MapPin, Plane, Train, Bus, Users, X, Sparkles, Search,
} from "lucide-react";
import TravelAutocompleteInput from "../search-form/TravelAutocompleteInput";
import { FEATURES } from "../../config/features";
import "../../styles/mobile-search.css";

/**
 * MobileSearchCard: the phone-only (≤768px) route search.
 *
 * It owns no search state. Everything lives in FormSearchSection and comes in
 * through props, so persistence, the travellers sheet and the /search-form URL
 * stay exactly as they are on desktop.
 *
 * Interaction model (same pattern big OTAs use):
 *   tap From → full-screen picker → pick → To picker opens → pick →
 *   date sheet opens (if no date yet) → pick → back on the card, one tap to search.
 * The Android/browser back button closes any open sheet instead of leaving the page.
 */

const RECENT_KEY = "rc_recent_locations";
const MAX_RECENT = 6;

const POPULAR_CITIES = ["Delhi", "Mumbai", "Bengaluru", "Chennai", "Kolkata", "Hyderabad", "Pune", "Ahmedabad"];

/* ── small helpers ── */
const cleanName = (txt) => String(txt || "").split("(")[0].trim();
const codeOf = (txt, data) => data?.code || (String(txt || "").match(/\(([^)]+)\)/) || [])[1] || "";
const kindOf = (data) => {
    if (!data) return "";
    if (data.subLabel) return data.subLabel;
    if (data.type === "airport") return "Airport";
    if (data.type === "station") return "Railway station";
    if (data.type === "bus" || data.type === "bus_stop") return "Bus stop";
    return "";
};
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = startOfDay(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => a && b && startOfDay(a).getTime() === startOfDay(b).getTime();
const fmtDay = (d) => d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
const relLabel = (d) => {
    const t = startOfDay(new Date());
    if (sameDay(d, t)) return "Today";
    if (sameDay(d, addDays(t, 1))) return "Tomorrow";
    if (sameDay(d, addDays(t, 2))) return "Day after tomorrow";
    return d.toLocaleDateString("en-IN", { weekday: "long" });
};

const readRecent = () => {
    try {
        const arr = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
        return Array.isArray(arr) ? arr.filter(x => x && x.label) : [];
    } catch { return []; }
};
const pushRecent = (item) => {
    if (!item?.label || item.type === "search-match") return;
    try {
        const next = [item, ...readRecent().filter(x => x.label !== item.label)].slice(0, MAX_RECENT);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch { /* storage blocked: recents are a convenience only */ }
};

/* ── Ghost-click guard ──
   The autocomplete selects on touchstart/pointerdown/mousedown (so it wins the
   race against input blur). We then unmount the sheet in that same tick, and
   the rest of the tap (mouseup + click) lands on whatever is now under the
   finger: usually the "Plan a trip" section behind the card, or a popular-city
   button on the next sheet. This swallows those trailing mouse events in the
   capture phase (before React or any <Link> sees them) until the finger lifts,
   plus a short grace period for the browser's emulated click. */
const GHOST_EVENTS = ["mousedown", "mouseup", "click"];
let ghostTimer = null;
const ghostBlock = (e) => { e.preventDefault(); e.stopPropagation(); };
const releaseGhostGuard = () => {
    clearTimeout(ghostTimer); ghostTimer = null;
    GHOST_EVENTS.forEach(t => window.removeEventListener(t, ghostBlock, true));
    window.removeEventListener("touchend", armGhostRelease, true);
    window.removeEventListener("pointerup", armGhostRelease, true);
};
function armGhostRelease() {
    clearTimeout(ghostTimer);
    ghostTimer = setTimeout(releaseGhostGuard, 400);
}
const swallowGhostClick = () => {
    if (typeof window === "undefined") return;
    releaseGhostGuard();
    GHOST_EVENTS.forEach(t => window.addEventListener(t, ghostBlock, true));
    window.addEventListener("touchend", armGhostRelease, true);
    window.addEventListener("pointerup", armGhostRelease, true);
    ghostTimer = setTimeout(releaseGhostGuard, 700); // hard cap if no up event follows
};

/* Lock page scroll while a full-screen sheet is open */
const useBodyLock = (on) => {
    useEffect(() => {
        if (!on) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [on]);
};

/* Sheets render at <body> level. Rendered inside the hero they inherit its
   stacking context (z-index 2) and the fixed navbar paints over them. */
const SheetPortal = ({ children }) =>
    typeof document !== "undefined" ? createPortal(children, document.body) : children;

/* ═══════════════════════════════════════════════════════════════════ */

const MobileSearchCard = ({
    fromLocation, toLocation, fromLocationData, toLocationData,
    setFrom, setTo, swap,
    travelDate, setTravelDate,
    activeModes, toggleMode,
    passengerCounts, seatClasses, openTravelers,
    nonStopOnly, setNonStopOnly,
    popularLocs,
    onSearch,
}) => {
    const navigate = useNavigate();
    const [sheet, setSheet] = useState(null); // null | "from" | "to" | "date"
    const [aiText, setAiText] = useState("");
    const [spin, setSpin] = useState(false);
    const pushedRef = useRef(false);

    /* Back button closes the sheet: push one history entry when a sheet opens,
       pop it when the sheet closes from the UI. Moving between sheets
       (from → to → date) reuses the same entry. */
    useEffect(() => {
        if (sheet && !pushedRef.current) {
            try { window.history.pushState({ ...(window.history.state || {}), rcSheet: true }, ""); } catch { }
            pushedRef.current = true;
        }
    }, [sheet]);
    useEffect(() => {
        const onPop = () => { if (pushedRef.current) { pushedRef.current = false; setSheet(null); } };
        window.addEventListener("popstate", onPop);
        return () => {
            window.removeEventListener("popstate", onPop);
            releaseGhostGuard();
        };
    }, []);
    const closeSheet = () => {
        setSheet(null);
        if (pushedRef.current) { pushedRef.current = false; try { window.history.back(); } catch { } }
    };

    /* After picking From, always hand over to the To tab (even if To is already
       filled) so the user can confirm or change it. To then decides: date sheet
       if no date yet, otherwise back to the card. */
    const pickFrom = (item) => {
        setFrom(item);
        pushRecent(item);
        setSheet("to");
    };
    const pickTo = (item) => {
        setTo(item);
        pushRecent(item);
        if (!travelDate) setSheet("date");
        else closeSheet();
    };
    const pickDate = (d) => { setTravelDate(d); closeSheet(); };

    /* Search: walk the user to whatever is missing instead of a dead disabled button */
    const handleSearch = () => {
        if (!fromLocation) return setSheet("from");
        if (!toLocation) return setSheet("to");
        if (!travelDate) return setSheet("date");
        onSearch();
    };

    const doSwap = () => { setSpin(s => !s); swap(); };

    const today = startOfDay(new Date());
    const quickDates = [
        { k: "Today", d: today },
        { k: "Tomorrow", d: addDays(today, 1) },
        { k: "Day after", d: addDays(today, 2) },
    ];

    /* Modes are multi-select. "All" is shown as every box ticked rather than as
       a separate option, so the UI reads like a checklist, not a tab bar.
       State stays the same shape FormSearchSection already uses (['All'] or a
       list), so the search URL is unchanged. */
    const modes = [
        { k: "Train", icon: Train, label: "Train" },
        { k: "Flight", icon: Plane, label: "Flight" },
        ...(FEATURES.ENABLE_BUS ? [{ k: "Bus", icon: Bus, label: "Bus" }] : []),
    ];
    const MODE_KEYS = modes.map(m => m.k);
    const allOn = activeModes.includes("All");
    const selectedModes = allOn ? MODE_KEYS : activeModes.filter(m => m !== "All");
    const [nudge, setNudge] = useState(null);
    const tapMode = (k) => {
        const isSel = selectedModes.includes(k);
        if (isSel && selectedModes.length === 1) {   // keep at least one mode
            setNudge(k); setTimeout(() => setNudge(null), 400);
            return;
        }
        if (allOn) {                                 // untick one out of "all"
            MODE_KEYS.filter(m => m !== k).forEach(toggleMode);
            return;
        }
        const next = isSel ? selectedModes.filter(m => m !== k) : [...selectedModes, k];
        if (next.length === MODE_KEYS.length) toggleMode("All");   // everything ticked = All
        else toggleMode(k);
    };

    /* travellers summary */
    const { adults = 1, children = 0, infants = 0 } = passengerCounts || {};
    const paxTotal = adults + children + infants;
    const showTrain = activeModes.includes("Train") || activeModes.includes("All");
    const showFlight = activeModes.includes("Flight") || activeModes.includes("All");
    const fl = seatClasses.filter(c => ["Economy", "Premium", "Business", "First"].includes(c));
    const tr = seatClasses.filter(c => ["1A", "2A", "3A", "SL"].includes(c));
    const classLine = [showFlight && fl.join(", "), showTrain && tr.join(", ")].filter(Boolean).join(" · ") || "Any class";

    const ready = fromLocation && toLocation && travelDate;

    return (
        <div className="rcm">
            <div className="rcm-card">
                {/* Modes: checklist chips */}
                <div className="rcm-modes-head">
                    <span className="rcm-modes-title" id="rcm-modes-title">Travel by</span>
                    <span className="rcm-modes-hint">{allOn ? "All modes" : "Choose one or more"}</span>
                </div>
                <div className="rcm-modes" role="group" aria-labelledby="rcm-modes-title">
                    {modes.map(({ k, icon: Icon, label }) => {
                        const on = selectedModes.includes(k);
                        return (
                            <button key={k} type="button" role="checkbox" aria-checked={on}
                                className={`rcm-mode${on ? " on" : ""}${nudge === k ? " nudge" : ""}`}
                                onClick={() => tapMode(k)}>
                                <span className="rcm-check" aria-hidden="true">
                                    {on && <Check size={12} strokeWidth={3.4} />}
                                </span>
                                <Icon size={16} strokeWidth={2.2} className="rcm-mode-ic" />
                                {label}
                            </button>
                        );
                    })}
                </div>

                {/* From / To */}
                <div className="rcm-od">
                    <LocRow kind="from" label="From" placeholder="Where from?"
                        text={fromLocation} data={fromLocationData} onTap={() => setSheet("from")} />
                    <div className="rcm-od-div" />
                    <LocRow kind="to" label="To" placeholder="Where to?"
                        text={toLocation} data={toLocationData} onTap={() => setSheet("to")} />
                    <span className="rcm-rail" aria-hidden="true" />
                    <button type="button" className={`rcm-swap${spin ? " spin" : ""}`}
                        aria-label="Swap origin and destination" onClick={doSwap}>
                        <ArrowUpDown size={18} strokeWidth={2.4} />
                    </button>
                </div>

                {/* Date | Travellers */}
                <div className="rcm-meta">
                    <button type="button" className="rcm-tile" onClick={() => setSheet("date")}>
                        <span className="rcm-lbl"><Calendar size={13} /> Departure</span>
                        {travelDate ? (
                            <>
                                <span className="rcm-val">{fmtDay(travelDate)}</span>
                                <span className="rcm-sub">{relLabel(travelDate)}</span>
                            </>
                        ) : (
                            <>
                                <span className="rcm-val rcm-ph">Add date</span>
                                <span className="rcm-sub">&nbsp;</span>
                            </>
                        )}
                    </button>
                    <span className="rcm-meta-sep" aria-hidden="true" />
                    <button type="button" className="rcm-tile" onClick={openTravelers}>
                        <span className="rcm-lbl"><Users size={13} /> Travellers</span>
                        <span className="rcm-val">{paxTotal} {paxTotal === 1 ? "Traveller" : "Travellers"}</span>
                        <span className="rcm-sub">{classLine}</span>
                    </button>
                </div>

                {/* Quick dates */}
                <div className="rcm-quick">
                    {quickDates.map(q => (
                        <button key={q.k} type="button"
                            className={`rcm-chip${sameDay(travelDate, q.d) ? " on" : ""}`}
                            onClick={() => setTravelDate(q.d)}>
                            {q.k}
                        </button>
                    ))}
                    <button type="button" className="rcm-chip rcm-chip-ghost" aria-label="More dates" onClick={() => setSheet("date")}>
                        <Calendar size={16} />
                    </button>
                </div>

                {/* Non-stop toggle: shown for every mode (All, Train, Flight, Bus) */}
                <label className="rcm-toggle">
                    <span>Non-stop only</span>
                    <input type="checkbox" checked={nonStopOnly} onChange={e => setNonStopOnly(e.target.checked)} />
                    <span className="rcm-switch" aria-hidden="true" />
                </label>

                <button type="button" className={`rcm-cta${ready ? "" : " soft"}`} onClick={handleSearch}>
                    <Search size={19} strokeWidth={2.6} />
                    {ready ? "Search routes" : !fromLocation ? "Choose where you start" : !toLocation ? "Choose your destination" : "Pick a date"}
                </button>
            </div>

            <p className="rcm-cover">
                <span className="rcm-cover-ic" aria-hidden="true">
                    <Train size={14} strokeWidth={2.2} /><Plane size={14} strokeWidth={2.2} /><Bus size={14} strokeWidth={2.2} />
                </span>
                Compares trains, flights and buses, with break journeys when direct is full.
            </p>

            {/* AI: describe the trip in your own words */}
            <form className="rcm-ai" onSubmit={(e) => {
                e.preventDefault();
                if (!aiText.trim()) return;
                navigate("/chat", { state: { fromHero: true, heroInput: aiText.trim() } });
            }}>
                <Sparkles size={18} className="rcm-ai-ic" fill="currentColor" strokeWidth={1.5} />
                <input
                    type="text" value={aiText} onChange={e => setAiText(e.target.value)}
                    placeholder="Or type it: Darbhanga se Mumbai kal"
                    enterKeyHint="send" aria-label="Describe your journey"
                />
                <button type="submit" aria-label="Ask AI" disabled={!aiText.trim()}>
                    <ChevronRight size={20} strokeWidth={2.6} />
                </button>
            </form>

            {/* Sheets */}
            {/* One LocationSheet instance for both tabs. No key={sheet}: a key
                change unmounts/remounts the sheet, which replays its enter
                animation and drops the body lock for a frame, so the page and
                navbar flash through when switching From ↔ To. */}
            {(sheet === "from" || sheet === "to") && (
                <LocationSheet
                    mode={sheet}
                    other={sheet === "from" ? toLocationData : fromLocationData}
                    popularLocs={popularLocs}
                    onClose={closeSheet}
                    onPick={sheet === "from" ? pickFrom : pickTo}
                    onSwitch={(m) => setSheet(m)}
                    fromText={fromLocation}
                    toText={toLocation}
                />
            )}
            {sheet === "date" && (
                <DateSheet selected={travelDate} onPick={pickDate} onClose={closeSheet}
                    routeLabel={fromLocation && toLocation ? `${cleanName(fromLocation)} → ${cleanName(toLocation)}` : ""} />
            )}
        </div>
    );
};

/* ── One From/To row on the card ── */
const LocRow = ({ kind, label, placeholder, text, data, onTap }) => {
    const name = cleanName(text);
    const code = codeOf(text, data);
    const sub = [code, kindOf(data)].filter(Boolean).join(" · ");
    return (
        <button type="button" className="rcm-loc" onClick={onTap}>
            <span className={`rcm-dot ${kind}`} aria-hidden="true" />
            <span className="rcm-loc-body">
                <span className="rcm-lbl">{label}</span>
                {name ? (
                    <>
                        <span className="rcm-loc-name">{name}</span>
                        {sub && <span className="rcm-sub">{sub}</span>}
                    </>
                ) : (
                    <span className="rcm-loc-name rcm-ph">{placeholder}</span>
                )}
            </span>
        </button>
    );
};

/* ═══ Full-screen location picker ═══════════════════════════════════ */
const LocationSheet = ({ mode, other, popularLocs, onClose, onPick, onSwitch, fromText, toText }) => {
    const [q, setQ] = useState("");
    const boxRef = useRef(null);
    useBodyLock(true);

    /* TravelAutocompleteInput draws its own suggestion panel. Depending on the
       component it is either an absolutely positioned child of the field OR
       portalled straight into <body>. We find it in both places at runtime and
       tag it .rcm-ac-list, so CSS can lift it above this sheet and pin it
       full-width under the search field. No dependency on its class names. */
    useLayoutEffect(() => {
        const box = boxRef.current;
        if (!box) return;
        const body = document.body;
        const root = document.getElementById("root");
        const tagged = new Set();
        body.classList.add("rcm-picker-open");

        const setTop = () => {
            const r = box.getBoundingClientRect();
            document.documentElement.style.setProperty("--rcm-list-top", `${Math.round(r.bottom + 8)}px`);
        };
        const looksLikeList = (el) =>
            !!el.querySelector("li,[role='option'],button,a,img,svg") || el.children.length >= 2;
        const isPositioned = (el) => {
            const pos = window.getComputedStyle(el).position;
            return pos === "absolute" || pos === "fixed";
        };
        /* Inside the pinned panel, any further absolute/fixed layers (the
           component's own dropdown box) go back into normal flow. */
        const flatten = (list) => list.querySelectorAll("*").forEach(d => {
            if (!d.classList.contains("rcm-ac-flow") && isPositioned(d)) { d.classList.add("rcm-ac-flow"); tagged.add(d); }
        });
        const mark = (el) => { el.classList.add("rcm-ac-list"); tagged.add(el); flatten(el); };

        /* 1. Panel rendered inside the field */
        const scanBox = () => {
            const input = box.querySelector("input");
            box.querySelectorAll(".rcm-ac-list").forEach(flatten);
            box.querySelectorAll(".rcm-ac *").forEach(el => {
                if (el.closest(".rcm-ac-list")) return;
                if (input && el.contains(input)) return;
                if (isPositioned(el) && looksLikeList(el)) mark(el);
            });
        };

        /* 2. Panel portalled into <body> (outside the app root and this sheet) */
        const SKIP = "script,style,link,iframe,noscript";
        const outsideApp = (el) =>
            el.nodeType === 1 && !el.matches(SKIP) &&
            !el.closest(".rcm-sheet") && !el.closest(".Toastify") &&
            !(root && root.contains(el));
        const scanNode = (node) => {
            if (!outsideApp(node)) return;
            const inList = node.closest(".rcm-ac-list");
            if (inList) { flatten(inList); return; }
            const cands = [node, ...node.querySelectorAll("*")];
            const hit = cands.find(el => isPositioned(el) && looksLikeList(el));
            if (hit) mark(hit);
            else if (node.querySelector("li,[role='option']")) mark(node);
        };

        setTop(); scanBox();
        Array.from(body.children).forEach(scanNode);

        const moBox = new MutationObserver(scanBox);
        moBox.observe(box, { childList: true, subtree: true });
        const moBody = new MutationObserver((muts) => {
            muts.forEach(m => m.addedNodes.forEach(n => {
                if (n.nodeType !== 1) return;
                // a node added inside an existing portal container: rescan that container
                scanNode(outsideApp(m.target) && m.target !== body ? m.target : n);
            }));
        });
        moBody.observe(body, { childList: true, subtree: true });
        window.addEventListener("resize", setTop);

        return () => {
            moBox.disconnect(); moBody.disconnect();
            window.removeEventListener("resize", setTop);
            body.classList.remove("rcm-picker-open", "rcm-typing");
            tagged.forEach(el => el.classList.remove("rcm-ac-list", "rcm-ac-flow"));
        };
    }, []);

    /* On open and on every From ↔ To switch: clear the query and focus the
       field. Runs as a layout effect, inside the tap's event, so iOS keeps the
       keyboard up instead of dropping and reopening it. */
    useLayoutEffect(() => {
        setQ("");
        const el = boxRef.current?.querySelector("input");
        if (el) { try { el.focus({ preventScroll: true }); } catch { el.focus(); } }
        const body = boxRef.current?.parentElement?.querySelector(".rcm-sheet-body");
        if (body) body.scrollTop = 0;
    }, [mode]);

    const recent = useMemo(() => readRecent().filter(r => !other || r.label !== other.label), [other]);
    const popular = useMemo(() => {
        const out = [];
        POPULAR_CITIES.forEach(city => {
            const hit = (popularLocs || []).find(l => l.city.toLowerCase() === city.toLowerCase())
                || (city === "Bengaluru" && (popularLocs || []).find(l => l.city === "Bangalore"));
            if (hit && (!other || hit.label !== other.label)) out.push({ ...hit, _display: city });
        });
        return out;
    }, [popularLocs, other]);

    const typing = q.trim().length > 0;
    useEffect(() => { document.body.classList.toggle("rcm-typing", typing); }, [typing]);

    /* Suggestion picked from the autocomplete panel: eat the rest of the tap
       before the sheet unmounts, so it can't fall through to the page. */
    const pickSuggestion = (item) => {
        swallowGhostClick();
        onPick(item);
    };

    return (
        <SheetPortal>
        <div className={`rcm-sheet${typing ? " typing" : ""}`} role="dialog" aria-modal="true" aria-label={mode === "from" ? "Choose origin" : "Choose destination"}>
            <div className="rcm-sheet-head">
                <button type="button" className="rcm-icon-btn" onClick={onClose} aria-label="Back">
                    <ArrowLeft size={22} />
                </button>
                <div className="rcm-seg" role="tablist">
                    <button type="button" role="tab" aria-selected={mode === "from"}
                        className={mode === "from" ? "on" : ""} onClick={() => mode !== "from" && onSwitch("from")}>
                        <small>From</small>{cleanName(fromText) || "Origin"}
                    </button>
                    <button type="button" role="tab" aria-selected={mode === "to"}
                        className={mode === "to" ? "on" : ""} onClick={() => mode !== "to" && onSwitch("to")}>
                        <small>To</small>{cleanName(toText) || "Destination"}
                    </button>
                </div>
            </div>

            <div className="rcm-search" ref={boxRef}>
                <Search size={18} className="rcm-search-ic" />
                <div className="rcm-ac">
                    <TravelAutocompleteInput
                        className="rcm-ac-input"
                        placeholder={mode === "from" ? "Search city, station or airport" : "Where are you going?"}
                        value={q}
                        onChange={(val) => setQ(val)}
                        onSelect={pickSuggestion}
                    />
                </div>
                {typing && (
                    <button type="button" className="rcm-icon-btn sm" aria-label="Clear" onClick={() => {
                        setQ("");
                        boxRef.current?.querySelector("input")?.focus();
                    }}>
                        <X size={16} />
                    </button>
                )}
            </div>

            <div className="rcm-sheet-body">
                {!typing && (
                    <>
                        {recent.length > 0 && (
                            <section className="rcm-sec">
                                <h4>Recent searches</h4>
                                {recent.map(r => (
                                    <button key={r.label} type="button" className="rcm-item" onClick={() => onPick(r)}>
                                        <span className="rcm-item-ic"><Clock size={16} /></span>
                                        <span className="rcm-item-body">
                                            <span className="rcm-item-name">{cleanName(r.label)}</span>
                                            <span className="rcm-item-sub">{kindOf(r) || r.city || ""}</span>
                                        </span>
                                        {codeOf(r.label, r) && <span className="rcm-code">{codeOf(r.label, r)}</span>}
                                    </button>
                                ))}
                            </section>
                        )}
                        <section className="rcm-sec">
                            <h4>Popular cities</h4>
                            <div className="rcm-pop">
                                {popular.map(p => (
                                    <button key={p.label} type="button" className="rcm-pop-btn" onClick={() => onPick(p)}>
                                        <MapPin size={14} /> {p._display}
                                    </button>
                                ))}
                            </div>
                        </section>
                        <p className="rcm-hint">
                            Tip: small towns work too. We'll find the nearest station, bus stop or airport and plan the connection.
                        </p>
                    </>
                )}
            </div>
        </div>
        </SheetPortal>
    );
};

/* ═══ Full-screen date picker (month list, OTA style) ═══════════════ */
const MONTHS_AHEAD = 6;
const DOW = ["S", "M", "T", "W", "T", "F", "S"];

const DateSheet = ({ selected, onPick, onClose, routeLabel }) => {
    useBodyLock(true);
    const today = startOfDay(new Date());
    const selRef = useRef(null);

    useLayoutEffect(() => {
        if (selRef.current) selRef.current.scrollIntoView({ block: "center" });
    }, []);

    const months = useMemo(() => {
        const out = [];
        for (let i = 0; i < MONTHS_AHEAD; i++) {
            const first = new Date(today.getFullYear(), today.getMonth() + i, 1);
            const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
            const cells = Array(first.getDay()).fill(null);
            for (let d = 1; d <= days; d++) cells.push(new Date(first.getFullYear(), first.getMonth(), d));
            out.push({ key: `${first.getFullYear()}-${first.getMonth()}`, title: first.toLocaleDateString("en-IN", { month: "long", year: "numeric" }), cells });
        }
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <SheetPortal>
        <div className="rcm-sheet" role="dialog" aria-modal="true" aria-label="Choose departure date">
            <div className="rcm-sheet-head">
                <button type="button" className="rcm-icon-btn" onClick={onClose} aria-label="Back">
                    <ArrowLeft size={22} />
                </button>
                <div className="rcm-sheet-title">
                    <strong>Departure date</strong>
                    {routeLabel && <span>{routeLabel}</span>}
                </div>
            </div>

            <div className="rcm-quick rcm-quick-sheet">
                {[["Today", 0], ["Tomorrow", 1], ["Day after", 2], ["This weekend", null]].map(([k, n]) => {
                    let d;
                    if (n === null) {
                        const sat = (6 - today.getDay() + 7) % 7;
                        d = addDays(today, sat === 0 ? 0 : sat);
                    } else d = addDays(today, n);
                    return (
                        <button key={k} type="button" className={`rcm-chip${sameDay(selected, d) ? " on" : ""}`} onClick={() => onPick(d)}>
                            {k}
                        </button>
                    );
                })}
            </div>

            <div className="rcm-dow">{DOW.map((d, i) => <span key={i} className={i === 0 || i === 6 ? "we" : ""}>{d}</span>)}</div>

            <div className="rcm-sheet-body rcm-cal">
                {months.map(m => (
                    <section key={m.key} className="rcm-month">
                        <h4>{m.title}</h4>
                        <div className="rcm-grid">
                            {m.cells.map((d, i) => {
                                if (!d) return <span key={i} />;
                                const past = d < today;
                                const sel = sameDay(d, selected);
                                const isToday = sameDay(d, today);
                                const we = d.getDay() === 0 || d.getDay() === 6;
                                return (
                                    <button key={i} type="button" disabled={past}
                                        ref={sel ? selRef : undefined}
                                        className={`rcm-day${sel ? " sel" : ""}${isToday ? " today" : ""}${we ? " we" : ""}`}
                                        onClick={() => onPick(d)}
                                        aria-label={d.toDateString()} aria-pressed={sel}>
                                        {d.getDate()}
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                ))}
            </div>
        </div>
        </SheetPortal>
    );
};

export default MobileSearchCard;
